import './style.scss';

import { Skeleton, Tooltip } from 'antd';
import React, {
  type MouseEvent,
  type ReactNode,
  createContext,
  useContext,
  useState
} from 'react';

import {
  RequestBodyInfo,
  type TRequestBodyInfoProps
} from '@/components/App/NewOrder/offer-step/entities';
import { useWindowSize } from '@/hooks/useWindowSize';
import Chevron from '@/media/icons/chevron.svg?react';
import EditIcon from '@/media/icons/edit.svg?react';
import InfoCircle from '@/media/svg/info-circle.svg?react';

import { Button, CircledImage, InfoBadge } from '..';
import { type INNER_STATUSES_ENUM } from '../../constants/statuses';

interface OfferCardContextProps {
  isOpen: boolean;
  toggleOpen: () => void;
}

const OfferCardContext = createContext<OfferCardContextProps | undefined>(
  undefined
);
const useOfferCardContext = () => {
  const context = useContext(OfferCardContext);
  if (!context) {
    throw new Error('useOfferCardContext must be used within OfferCard');
  }
  return context;
};
interface HeaderActionInterface<T> {
  onClick: (value: T) => void;
  text: string | React.ReactElement;
}

type OfferStatus = keyof typeof INNER_STATUSES_ENUM;

interface OfferCardProps {
  status?: OfferStatus;
  statusText?: string;
  isSelected?: boolean;
  extId?: string;
  children: ReactNode;
  className?: string;
  onEditStatus?: () => void;
  isLeasing?: boolean;
}

const OfferCard: React.FC<OfferCardProps> & {
  Header: typeof Header;
  Body: typeof Body;
} = ({
  children,
  extId,
  status,
  statusText,
  isSelected,
  className,
  onEditStatus,
  isLeasing = false
}) => {
  const offerCardSelected = isSelected ? ` offer-card--selected` : '';
  const idStatusClass = status ? `offer-card__id--${status}` : '';
  const statusClass = status ? `offer-card__status--${status}` : '';

  const [isOpen, setIsOpen] = useState(false);
  const toggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  const classes = [
    'offer-card',
    offerCardSelected,
    !isSelected && status === 'disabled' ? ' offer-card--disabled' : '',
    !isSelected && status === 'processing' ? ' offer-card--processing' : '',
    className ? ` ${className}` : ''
  ].join('');

  return (
    <OfferCardContext.Provider value={{ isOpen, toggleOpen }}>
      <div className={classes}>
        <div className="offer-card__left-absolute">
          {status && (
            <div
              role={onEditStatus ? 'button' : undefined}
              className={`offer-card__status ${statusClass}`}
              onClick={onEditStatus}
            >
              {statusText}
              {onEditStatus && <EditIcon />}
            </div>
          )}
          {isLeasing && (
            <div className="offer-card__status-leasing">
              <p>Лизинг</p>
            </div>
          )}
          {extId && (
            <div
              className={`offer-card__id ${idStatusClass}${offerCardSelected} `}
            >
              <p>{extId}</p>
            </div>
          )}
        </div>
        {children}
      </div>
    </OfferCardContext.Provider>
  );
};

interface HeaderProps {
  defaultMobileView?: boolean;
  descriptionAction?: HeaderActionInterface<MouseEvent<HTMLButtonElement>>;
  headerActions?: React.ReactNode[];
  icon?: string;
  info?: string;
  isWaitingOffer?: boolean;
  requestBodyInfo?: TRequestBodyInfoProps;
  status?: OfferStatus;
  title: string | undefined;
}

const Header: React.FC<HeaderProps> = ({
  defaultMobileView = true,
  descriptionAction,
  headerActions,
  icon,
  info,
  isWaitingOffer,
  requestBodyInfo,
  status,
  title = ''
}) => {
  const { isOpen, toggleOpen } = useOfferCardContext();
  const shouldShowChevron = !isWaitingOffer && status !== 'error';

  return (
    <div
      className="offer-header"
      onClick={shouldShowChevron ? toggleOpen : undefined}
    >
      <div className="offer-header__title-wrapper">
        {icon && <CircledImage src={icon} alt={title} size={32} />}
        <h2 className="offer-header__title">{title}</h2>
      </div>

      <div className="offer-header__actions">
        {!!requestBodyInfo && (
          <RequestBodyInfo
            bankName={requestBodyInfo.bankName}
            responsePrimaryBody={requestBodyInfo.responsePrimaryBody}
            responseSendedBody={requestBodyInfo.responseSendedBody}
          />
        )}
        {descriptionAction && (
          <Button size="small" onClick={descriptionAction.onClick}>
            <InfoCircle /> {descriptionAction.text}
          </Button>
        )}
        {isWaitingOffer && (
          <Skeleton.Input active style={{ width: '187px', height: '20px' }} />
        )}
        {/* TODO -> сделать стили для info-badge display none */}
        {info && <InfoBadge title={info} />}

        <div
          className={`offer-header__actions-list ${
            defaultMobileView ? 'offer-header__actions-list--hidden' : ''
          }`}
        >
          {headerActions}
        </div>

        {shouldShowChevron && (
          <div
            className={`offer-header__chevron-mobile ${
              isOpen ? 'offer-header__chevron-mobile--active' : ''
            }`}
          >
            <Chevron />
          </div>
        )}
      </div>
    </div>
  );
};

interface BodyProps {
  additionalProducts?: {
    title: string | React.ReactNode;
    content: string[];
  };
  footerActions?: React.ReactNode[];
  products: {
    isRed?: boolean;
    title: string;
    description: string;
    subInto?: {
      title: string;
      description: string;
    };
  }[];
}

const Body: React.FC<BodyProps> = ({
  additionalProducts,
  products,
  footerActions
}) => {
  const { isOpen } = useOfferCardContext();
  const [windowWidth] = useWindowSize();

  const lastThreeProductsStartIndex = products.length - 3;
  const mobileWidth = 860;

  const mainProducts =
    windowWidth <= mobileWidth
      ? products.slice(0, lastThreeProductsStartIndex)
      : products;
  const mobileProducts =
    windowWidth <= mobileWidth && isOpen
      ? products.slice(lastThreeProductsStartIndex)
      : [];
  return (
    <div
      className={`offer-body ${isOpen ? 'offer-body--open' : 'offer-body--closed'}`}
    >
      <div
        className={`offer-body__product ${
          isOpen
            ? 'offer-body__product--visible'
            : 'offer-body__product--hidden'
        }`}
      >
        {mainProducts.map((product, index) => (
          <div
            className={`offer-body__product-item ${index < lastThreeProductsStartIndex ? 'offer-body__product-item--bold' : ''}`}
            key={product.title + index}
          >
            <h4 className="offer-body__product-title">{product.title}</h4>
            <p
              className={`offer-body__product-description ${product.isRed ? 'offer-body__product-description--red' : ''}`}
            >
              {product.description}
              {product.subInto && (
                <Tooltip
                  title={
                    <>
                      <h4>{product.subInto.title}</h4>
                      <p className="offer-body__product-sub-description">
                        {product.subInto.description}
                      </p>
                    </>
                  }
                >
                  <InfoCircle />
                </Tooltip>
              )}
            </p>
          </div>
        ))}
      </div>
      {mobileProducts.length > 0 &&
        mobileProducts.map((product) => (
          <div className="offer-body__product-mobile-item" key={product.title}>
            <h4 className="offer-body__product-mobile-title">
              {product.title}
            </h4>
            <p className="offer-body__product-mobile-description">
              {product.description}
            </p>
          </div>
        ))}

      {additionalProducts &&
        additionalProducts.content.length > 0 &&
        (isOpen || windowWidth > mobileWidth) && (
          <div className="offer-body__additional">
            <h4 className="offer-body__additional-title">
              {additionalProducts.title}
            </h4>
            <ul className="offer-body__additional-list">
              {additionalProducts.content.map((content) => (
                <li className="offer-body__additional-item" key={content}>
                  {content}
                </li>
              ))}
            </ul>
          </div>
        )}
      <div className="offer-body__actions">{footerActions}</div>
    </div>
  );
};

OfferCard.Header = Header;
OfferCard.Body = Body;

export default OfferCard;
